import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Settings, FileSearch, MessageSquare, Save, RotateCcw, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function VBREngineManager() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<any>(null);

  // Fetch VBR settings
  const { data: vbrSettings, isLoading } = useQuery<any>({
    queryKey: ["/api/vbr-settings"],
  });

  useEffect(() => {
    if (vbrSettings) {
      setSettings(vbrSettings);
    }
  }, [vbrSettings]);

  // Save VBR settings mutation
  const saveSettings = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest("/api/vbr-settings", "PUT", data);
    },
    onSuccess: () => {
      toast({
        title: "Settings saved",
        description: "VBR Engine settings have been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/vbr-settings"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save settings",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    if (settings) {
      // Remove auto-generated fields before saving
      const { id, createdAt, updatedAt, ...settingsToSave } = settings;
      saveSettings.mutate(settingsToSave);
    }
  };

  const resetToDefaults = () => {
    const defaults = {
      ...settings,
      stationDocumentRatio: 0.5,
      targetFactCount: 4,
    };
    setSettings(defaults);
  };

  if (isLoading) {
    return <div>Loading VBR settings...</div>;
  }

  if (!settings) {
    return <div>No settings available</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          VBR Engine Management
        </CardTitle>
        <CardDescription>
          Configure the prompts and parameters that control VBR generation across all stations
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="prompts" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="prompts" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Prompts
            </TabsTrigger>
            <TabsTrigger value="rag" className="flex items-center gap-2">
              <FileSearch className="h-4 w-4" />
              RAG Settings
            </TabsTrigger>
            <TabsTrigger value="websearch" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Web Search
            </TabsTrigger>
          </TabsList>

          <TabsContent value="prompts" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="systemPrompt">System Prompt</Label>
              <Textarea
                id="systemPrompt"
                value={settings.systemPrompt}
                onChange={(e) => setSettings({ ...settings, systemPrompt: e.target.value })}
                className="min-h-[200px] font-mono text-sm"
                placeholder="Enter the system prompt for VBR generation..."
              />
              <p className="text-xs text-gray-500">
                This prompt defines how the AI generates VBR emails. It includes the perspective, requirements, and output format.
                <br /><br />
                <strong>Available placeholders for VBR generation:</strong><br />
                • {'{selectedFact}'} - The fact text that was selected for the VBR<br />
                • {'{sourceText}'} - The source citation for the selected fact
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyAnalysisPrompt">Company Analysis Prompt</Label>
              <Textarea
                id="companyAnalysisPrompt"
                value={settings.companyAnalysisPrompt}
                onChange={(e) => setSettings({ ...settings, companyAnalysisPrompt: e.target.value })}
                className="min-h-[150px] font-mono text-sm"
                placeholder="Enter the company analysis prompt..."
              />
              <p className="text-xs text-gray-500">
                This prompt analyzes companies to extract keywords for better fact retrieval. Use {'{companyName}'} and {'{websiteUrl}'} as placeholders.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="factSelectionPrompt">Fact Selection Prompt</Label>
              <Textarea
                id="factSelectionPrompt"
                value={settings.factSelectionPrompt || ''}
                onChange={(e) => setSettings({ ...settings, factSelectionPrompt: e.target.value })}
                className="min-h-[300px] font-mono text-sm"
                placeholder="Enter the fact selection prompt..."
              />
              <p className="text-xs text-gray-500">
                This prompt is sent to GPT-5 to select the most relevant facts from candidates. Available placeholders:
              </p>
              <p className="text-xs text-gray-400 mt-1">
                • {'{companyName}'} - The company name being analyzed<br />
                • {'{industry}'} - The detected industry<br />
                • {'{targetAudience}'} - The target audience<br />
                • {'{additionalContext}'} - Any additional context provided<br />
                • {'{targetStationFacts}'} - Number of station facts to select<br />
                • {'{targetGlobalFacts}'} - Number of global facts to select<br />
                • {'{targetWebFacts}'} - Number of web facts to select<br />
                • {'{factsForPrompt}'} - The numbered list of all candidate facts
              </p>
            </div>

          </TabsContent>

          <TabsContent value="rag" className="space-y-4 mt-4">
            <div className="grid gap-6">
              {/* Web Research Toggle */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="enableWebResearch">Enable Web Research</Label>
                    <p className="text-xs text-gray-500">
                      Search the web for real facts when not enough are found in the knowledge base
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Uses Tavily API for real web search with verifiable sources and clickable URLs
                    </p>
                  </div>
                  <Switch
                    id="enableWebResearch"
                    checked={settings.enableWebResearch || false}
                    onCheckedChange={(checked) => setSettings({ ...settings, enableWebResearch: checked })}
                  />
                </div>
              </div>

              {/* Reasoning Effort Settings */}
              <div className="space-y-4 border-t pt-4">
                <Label className="text-base font-semibold">AI Reasoning Effort Settings</Label>
                <p className="text-xs text-gray-500 mb-4">
                  Configure the reasoning effort level for different AI operations.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* VBR Email Reasoning */}
                  <div className="space-y-2">
                    <Label htmlFor="reasoningVbrEmail" className="text-sm">VBR Email Generation</Label>
                    <Select
                      value={settings.reasoningVbrEmail || 'high'}
                      onValueChange={(value) => setSettings({ ...settings, reasoningVbrEmail: value })}
                    >
                      <SelectTrigger id="reasoningVbrEmail" data-testid="select-reasoning-vbr-email">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low" data-testid="option-reasoning-vbr-low">Low (Faster, Lower Cost)</SelectItem>
                        <SelectItem value="medium" data-testid="option-reasoning-vbr-medium">Medium (Balanced)</SelectItem>
                        <SelectItem value="high" data-testid="option-reasoning-vbr-high">High (Best Quality)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Controls the quality of VBR email generation (customer-facing content)
                    </p>
                  </div>

                  {/* Relevance Scoring Reasoning */}
                  <div className="space-y-2">
                    <Label htmlFor="reasoningFactSelection" className="text-sm">Relevance Scoring</Label>
                    <Select
                      value={settings.reasoningFactSelection || 'medium'}
                      onValueChange={(value) => setSettings({ ...settings, reasoningFactSelection: value })}
                    >
                      <SelectTrigger id="reasoningFactSelection" data-testid="select-reasoning-fact-selection">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low" data-testid="option-reasoning-fact-low">Low (Basic Filtering)</SelectItem>
                        <SelectItem value="medium" data-testid="option-reasoning-fact-medium">Medium (Smart Selection)</SelectItem>
                        <SelectItem value="high" data-testid="option-reasoning-fact-high">High (Advanced Analysis)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Controls how AI evaluates and scores fact relevance for your prospect
                    </p>
                  </div>

                  {/* Fact Search Reasoning */}
                  <div className="space-y-2">
                    <Label htmlFor="reasoningFactDiscovery" className="text-sm">Fact Search</Label>
                    <Select
                      value={settings.reasoningFactDiscovery || 'medium'}
                      onValueChange={(value) => setSettings({ ...settings, reasoningFactDiscovery: value })}
                    >
                      <SelectTrigger id="reasoningFactDiscovery" data-testid="select-reasoning-discovery">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low" data-testid="option-reasoning-discovery-low">Low (Basic Search)</SelectItem>
                        <SelectItem value="medium" data-testid="option-reasoning-discovery-medium">Medium (Enhanced Search)</SelectItem>
                        <SelectItem value="high" data-testid="option-reasoning-discovery-high">High (Deep Discovery)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Controls how comprehensively the system searches your knowledge base
                    </p>
                  </div>

                  {/* Company Analysis Reasoning */}
                  <div className="space-y-2">
                    <Label htmlFor="reasoningCompanyAnalysis" className="text-sm">Company Analysis</Label>
                    <Select
                      value={settings.reasoningCompanyAnalysis || 'medium'}
                      onValueChange={(value) => setSettings({ ...settings, reasoningCompanyAnalysis: value })}
                    >
                      <SelectTrigger id="reasoningCompanyAnalysis" data-testid="select-reasoning-analysis">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low" data-testid="option-reasoning-analysis-low">Low (Basic Analysis)</SelectItem>
                        <SelectItem value="medium" data-testid="option-reasoning-analysis-medium">Medium (Detailed Analysis)</SelectItem>
                        <SelectItem value="high" data-testid="option-reasoning-analysis-high">High (Comprehensive Analysis)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Controls keyword extraction and company understanding depth
                    </p>
                  </div>
                </div>
              </div>

              {/* Fact Relevance Filter - Removed, hard-coded to 50% with fallback */}
              {/* Facts Provided to AI - Removed to allow AI to consider all available facts */}

              <div className="space-y-4">
                <Label className="text-base font-semibold">Fact Source Distribution</Label>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-sm">Station Facts</Label>
                    <span className="text-sm font-medium">{((settings.stationFactRatio ?? 0.4) * 100).toFixed(0)}%</span>
                  </div>
                  <Slider
                    value={[(settings.stationFactRatio ?? 0.4) * 100]}
                    onValueChange={(value) => {
                      const stationRatio = value[0] / 100;
                      const remaining = 1 - stationRatio;
                      const globalRatio = settings.globalFactRatio ?? 0.4;
                      const webRatio = settings.webFactRatio ?? 0.2;
                      const otherTotal = globalRatio + webRatio;
                      if (otherTotal === 0) {
                        setSettings({ 
                          ...settings, 
                          stationFactRatio: stationRatio,
                          globalFactRatio: remaining * 0.5,
                          webFactRatio: remaining * 0.5
                        });
                      } else {
                        const globalProportion = globalRatio / otherTotal;
                        setSettings({ 
                          ...settings, 
                          stationFactRatio: stationRatio,
                          globalFactRatio: remaining * globalProportion,
                          webFactRatio: remaining * (1 - globalProportion)
                        });
                      }
                    }}
                    min={0}
                    max={100}
                    step={10}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Station-specific content like ratings and demographics
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-sm">Global Facts</Label>
                    <span className="text-sm font-medium">{((settings.globalFactRatio ?? 0.4) * 100).toFixed(0)}%</span>
                  </div>
                  <Slider
                    value={[(settings.globalFactRatio ?? 0.4) * 100]}
                    onValueChange={(value) => {
                      const globalRatio = value[0] / 100;
                      const remaining = 1 - globalRatio;
                      const stationRatio = settings.stationFactRatio ?? 0.4;
                      const webRatio = settings.webFactRatio ?? 0.2;
                      const otherTotal = stationRatio + webRatio;
                      if (otherTotal === 0) {
                        setSettings({ 
                          ...settings, 
                          globalFactRatio: globalRatio,
                          stationFactRatio: remaining * 0.5,
                          webFactRatio: remaining * 0.5
                        });
                      } else {
                        const stationProportion = stationRatio / otherTotal;
                        setSettings({ 
                          ...settings, 
                          globalFactRatio: globalRatio,
                          stationFactRatio: remaining * stationProportion,
                          webFactRatio: remaining * (1 - stationProportion)
                        });
                      }
                    }}
                    min={0}
                    max={100}
                    step={10}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Industry facts, awareness days, and general trends
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-sm">Web Search Facts</Label>
                    <span className="text-sm font-medium">{((settings.webFactRatio ?? 0.2) * 100).toFixed(0)}%</span>
                  </div>
                  <Slider
                    value={[(settings.webFactRatio ?? 0.2) * 100]}
                    onValueChange={(value) => {
                      const webRatio = value[0] / 100;
                      const remaining = 1 - webRatio;
                      const stationRatio = settings.stationFactRatio ?? 0.4;
                      const globalRatio = settings.globalFactRatio ?? 0.4;
                      const otherTotal = stationRatio + globalRatio;
                      if (otherTotal === 0) {
                        setSettings({ 
                          ...settings, 
                          webFactRatio: webRatio,
                          stationFactRatio: remaining * 0.5,
                          globalFactRatio: remaining * 0.5
                        });
                      } else {
                        const stationProportion = stationRatio / otherTotal;
                        setSettings({ 
                          ...settings, 
                          webFactRatio: webRatio,
                          stationFactRatio: remaining * stationProportion,
                          globalFactRatio: remaining * (1 - stationProportion)
                        });
                      }
                    }}
                    min={0}
                    max={100}
                    step={10}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Real-time web data from Tavily search with verifiable sources
                  </p>
                </div>

                <p className="text-xs text-gray-400 mt-2 border-t pt-2">
                  <strong>Note:</strong> These percentages automatically adjust to always sum to 100%. Web facts require web research to be enabled above.
                </p>
              </div>

              {/* Minimum Facts Required - Removed, not being used */}

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Target Fact Count</Label>
                  <span className="text-sm font-medium">{settings.targetFactCount} facts</span>
                </div>
                <Slider
                  value={[settings.targetFactCount]}
                  onValueChange={(value) => setSettings({ ...settings, targetFactCount: value[0] })}
                  min={2}
                  max={30}
                  step={1}
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Target number of facts to include in each VBR (2-30)
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  <strong>More facts:</strong> More compelling VBRs with multiple proof points, but may feel data-heavy
                  <br />
                  <strong>Fewer facts:</strong> Cleaner, more focused VBRs, but less supporting evidence
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Max Total Candidate Facts</Label>
                  <span className="text-sm font-medium">{settings.maxCandidateFacts ?? 100} facts</span>
                </div>
                <Slider
                  value={[settings.maxCandidateFacts ?? 100]}
                  onValueChange={(value) => setSettings({ ...settings, maxCandidateFacts: value[0] })}
                  min={50}
                  max={500}
                  step={25}
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Total candidate facts to retrieve before AI selection (50-500)
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  <strong>More candidates:</strong> Better chance of finding niche facts (like household income), but higher AI cost
                  <br />
                  <strong>Fewer candidates:</strong> Faster and cheaper, but may miss relevant facts with lower similarity scores
                </p>
              </div>

              {/* GPT-5 specific parameters and temperature removed - not compatible with standard API */}
            </div>
          </TabsContent>

          <TabsContent value="websearch" className="space-y-4 mt-4">
            <div className="grid gap-6">
              <div className="space-y-2">
                <Label htmlFor="searchQueryPattern">Search Query Pattern</Label>
                <Textarea
                  id="searchQueryPattern"
                  value={settings.searchQueryPattern || "{industry} {websiteInfo} industry statistics market size demographics revenue growth trends customer data"}
                  onChange={(e) => setSettings({ ...settings, searchQueryPattern: e.target.value })}
                  className="min-h-[100px] font-mono text-sm"
                  placeholder="Enter search query pattern..."
                />
                <p className="text-xs text-gray-500">
                  Template for Tavily search queries. Use {'{industry}'} and {'{websiteInfo}'} as placeholders.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Company name is excluded to avoid search mismatches. The {'{websiteInfo}'} placeholder includes key terms from the company's website.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Max Search Results</Label>
                  <span className="text-sm font-medium">{settings.searchMaxResults || 5} results</span>
                </div>
                <Slider
                  value={[settings.searchMaxResults || 5]}
                  onValueChange={(value) => setSettings({ ...settings, searchMaxResults: value[0] })}
                  min={3}
                  max={10}
                  step={1}
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Number of search results to fetch from Tavily (3-10)
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  <strong>More results:</strong> Better chance of finding relevant facts, but slower and costs more API calls
                  <br />
                  <strong>Fewer results:</strong> Faster processing, lower cost, but may miss relevant information
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="webFactExtractionPrompt">Web Fact Extraction Prompt</Label>
                <Textarea
                  id="webFactExtractionPrompt"
                  value={settings.webFactExtractionPrompt || `Extract up to {webFactsToExtract} specific, verifiable statistics DIRECTLY RELATED to the {industry} industry.

Company: {companyName}
Industry: {industry}

Search Results:
{searchContent}

CRITICAL RELEVANCE FILTER - REJECT facts about:
- General economy, unemployment, or job market (unless specifically about {industry} industry jobs)
- Unrelated industries (e.g., don't include wine stats for an axe throwing venue)
- Generic business statistics not specific to {industry}
- Political events, stock markets, or global news
- Any topic not directly connected to {industry} or {companyName}

ACCEPT ONLY facts about:
- {industry} or {companyName} industry statistics, growth, revenue
- Customer demographics specifically for {industry} businesses
- Market trends directly affecting {industry} companies
- Consumer behavior related to {industry} services/products
- Competition and market size for {industry} sector

EXTRACTION REQUIREMENTS:
1. Extract ONLY facts from the search results above - never make up data
2. Each fact must be a complete, grammatically correct sentence
3. Facts must contain specific statistics, percentages, or numerical data
4. Clean up any formatting issues or incomplete sentences
5. Each fact should be 15-50 words and self-contained
6. Use exact URLs from the search results

Return JSON: {"facts": [{"fact": "...", "source": "...", "url": "..."}]}

Example for axe throwing company:
GOOD: "The axe throwing industry generates $215 million annually in the United States."
BAD: "The US unemployment rate rose to 4.3% in August 2024."`}
                  onChange={(e) => setSettings({ ...settings, webFactExtractionPrompt: e.target.value })}
                  className="min-h-[400px] font-mono text-xs"
                  placeholder="Enter the GPT-4 prompt for extracting facts from web search results..."
                />
                <p className="text-xs text-gray-500">
                  GPT-4 prompt for extracting facts from Tavily search results. Available placeholders:
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  <code>{'{webFactsToExtract}'}</code> - Number of facts to extract<br/>
                  <code>{'{companyName}'}</code> - Company name from user input<br/>
                  <code>{'{industry}'}</code> - Industry from user input<br/>
                  <code>{'{searchContent}'}</code> - Raw search results from Tavily
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={resetToDefaults}
            className="flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset RAG to Defaults
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveSettings.isPending}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saveSettings.isPending ? "Saving..." : "Save All Settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}